/**
 * Ask Lilia in its own window (desktop, 10 Oct): the conversation and the
 * editor talk through window events (`lilia:document-applied` reloads the
 * editor, `lilia:ask-improve` prefills Ask Lilia, …). In one page that is all it
 * takes; across two windows the same events are carried over a
 * BroadcastChannel, one per document, so neither side changes.
 *
 * Both ways out use it: a new window loads the Ask page, and Keep on top
 * (Document Picture-in-Picture) shows the same page in a frame, its own page,
 * so its menus and styles are its own rather than the opener's.
 */
import { AI_CHANGES_EVENT } from "@/lib/mobileChanges";
import { EDITOR_COMMAND_EVENT } from "@/lib/editorCommands";

/** The events each side sends to the other. */
export const FROM_ASK = ["lilia:document-applied", "lilia:ai-editing", AI_CHANGES_EVENT, "lilia:ask-apply-latex", EDITOR_COMMAND_EVENT] as const;
export const FROM_EDITOR = ["lilia:ask-improve", "lilia:ask-context"] as const;

/** Window presence and "Bring back", outside the forwarded events. */
export type BridgeSignal = "opened" | "closed" | "bring-back";

type Message = { kind: "event"; type: string; detail: unknown } | { kind: "signal"; signal: BridgeSignal };

export function askChannelName(documentId: string): string {
  return `lilia-ask:${documentId}`;
}

/** An event this bridge re-dispatched: not sent back, or the windows would echo forever. */
const BRIDGED = Symbol("lilia-bridged");

export interface AskBridge {
  signal: (s: BridgeSignal) => void;
  close: () => void;
}

/**
 * Forward this side's events to the other window and replay the other side's
 * here. `onSignal` hears the other window open, close, or ask to come back.
 */
export function startAskBridge(
  documentId: string,
  side: "editor" | "ask",
  onSignal?: (s: BridgeSignal) => void,
  target: Window = window,
): AskBridge | null {
  if (typeof BroadcastChannel === "undefined") return null;
  const channel = new BroadcastChannel(askChannelName(documentId));
  const outgoing = side === "ask" ? FROM_ASK : FROM_EDITOR;

  const forward = (e: Event) => {
    if ((e as Event & { [BRIDGED]?: boolean })[BRIDGED]) return;
    const detail = (e as CustomEvent).detail;
    // Only plain data crosses: functions and DOM nodes cannot be cloned.
    let safe: unknown = detail;
    try {
      safe = detail === undefined ? undefined : JSON.parse(JSON.stringify(detail));
    } catch {
      return;
    }
    channel.postMessage({ kind: "event", type: e.type, detail: safe } satisfies Message);
  };
  for (const type of outgoing) target.addEventListener(type, forward);

  channel.onmessage = (m: MessageEvent<Message>) => {
    const msg = m.data;
    if (!msg || typeof msg !== "object") return;
    if (msg.kind === "signal") {
      onSignal?.(msg.signal);
      return;
    }
    const incoming = side === "ask" ? FROM_EDITOR : FROM_ASK;
    if (!(incoming as readonly string[]).includes(msg.type)) return;
    const ev = new CustomEvent(msg.type, { detail: msg.detail });
    (ev as CustomEvent & { [BRIDGED]?: boolean })[BRIDGED] = true;
    target.dispatchEvent(ev);
  };

  return {
    signal: (s) => channel.postMessage({ kind: "signal", signal: s } satisfies Message),
    close: () => {
      for (const type of outgoing) target.removeEventListener(type, forward);
      channel.close();
    },
  };
}

/** Whether this browser can keep a window on top (Document Picture-in-Picture: Chrome, Edge). */
export function canKeepOnTop(): boolean {
  return typeof window !== "undefined" && "documentPictureInPicture" in window;
}
